import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AgentLayout from './AgentLayout';
import Icon from '../../components/AppIcon';
import {
  selectCurrentUser,
  selectCustomerId,
} from '../../features/auth/authSlice';
import {
  useGetMyAgentPropertiesQuery,
  useGetWorkspaceDashboardQuery,
  useGetVerificationStatusQuery,
  useGetCrmLeadsQuery,
  useGetCoBrokerAgreementsQuery,
  useGetMonetizationConfigQuery,
} from '../../features/api/apiSlice';
import { useCurrency } from '../../features/currency/CurrencyContext';

const AgentDashboard = () => {
  const user = useSelector(selectCurrentUser);
  const customerId = useSelector(selectCustomerId);
  const { formatPrice } = useCurrency();
  const { data: monetization } = useGetMonetizationConfigQuery();

  const { data: propsData, isLoading: propsLoading } = useGetMyAgentPropertiesQuery(
    { userid: customerId, limit: 8 },
    { skip: !customerId }
  );
  const { data: workspace } = useGetWorkspaceDashboardQuery(undefined, {
    skip: !customerId,
  });
  const { data: verification } = useGetVerificationStatusQuery(undefined, {
    skip: !customerId,
  });
  const { data: leads = [] } = useGetCrmLeadsQuery(undefined, { skip: !customerId });
  const { data: coBrokers = [] } = useGetCoBrokerAgreementsQuery(undefined, {
    skip: !customerId,
  });

  const properties = propsData?.data || [];
  const totalListings = propsData?.total_count ?? properties.length;
  const activeListings = properties.filter((p) => Number(p.status) === 1).length;
  const openLeads = leads.filter((l) => !['won', 'lost'].includes(l.status)).length;
  const activeDeals = coBrokers.filter((c) => c.status === 'active').length;

  const tools = [
    {
      to: '/agent-dashboard/properties',
      icon: 'Building2',
      title: 'My Properties',
      desc: 'Add, edit visibility, and manage your portfolio',
      accent: 'bg-blue-50 text-blue-700',
    },
    {
      to: '/workspace/crm',
      icon: 'Contact',
      title: 'CRM',
      desc: 'Leads, pipeline, and follow-up reminders',
      accent: 'bg-emerald-50 text-emerald-700',
    },
    {
      to: '/workspace/co-broker',
      icon: 'Users',
      title: 'Co-Brokering',
      desc: 'Split commissions and sign partner agreements',
      accent: 'bg-violet-50 text-violet-700',
    },
    {
      to: '/workspace/bookings',
      icon: 'Calendar',
      title: 'Bookings',
      desc: 'Availability calendar and consultations',
      accent: 'bg-amber-50 text-amber-800',
    },
    {
      to: '/workspace/documents',
      icon: 'FileText',
      title: 'E-Sign',
      desc: 'Viewing forms, LOI, and digital signatures',
      accent: 'bg-rose-50 text-rose-700',
    },
    {
      to: '/notice-board',
      icon: 'ClipboardList',
      title: 'Notice Board',
      desc: 'Respond to live property needs from users',
      accent: 'bg-cyan-50 text-cyan-800',
    },
    {
      to: '/agent-dashboard/promote',
      icon: 'Megaphone',
      title: 'Promote',
      desc: 'Leads, featured directory, video tours, and ads',
      accent: 'bg-orange-50 text-orange-800',
    },
  ];

  return (
    <AgentLayout
      title={`Good day, ${user?.name?.split(' ')[0] || 'Agent'}`}
      subtitle="Your professional command center — properties, clients, deals, and collaboration."
    >
      {/* Free mode / monetization status */}
      {monetization && !monetization.subscriptions_enabled && (
        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <Icon name="Gift" size={20} className="text-emerald-600 mt-0.5" />
            <div>
              <p className="font-semibold text-emerald-900 text-sm">Free agent access</p>
              <p className="text-xs text-emerald-800/80">
                Listing is free — however your listings have to be verified.
                {monetization.boost_enabled
                  ? ' Boosted placement is available as a paid add-on.'
                  : ' You can still subscribe for extra listing limits and placement.'}
              </p>
            </div>
          </div>
          <Link
            to="/agent-dashboard/subscription"
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800"
          >
            View plans
          </Link>
        </div>
      )}
      {monetization?.subscriptions_enabled && (
        <div className="mb-6 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <Icon name="Crown" size={20} className="text-violet-600 mt-0.5" />
            <div>
              <p className="font-semibold text-violet-900 text-sm">Subscriptions are enforced</p>
              <p className="text-xs text-violet-800/80">
                Free plan allows up to {monetization.free_property_limit_when_enforced ?? 3}{' '}
                listings. Upgrade to Pro for unlimited listings and premium placement.
              </p>
            </div>
          </div>
          <Link
            to="/agent-dashboard/subscription"
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700"
          >
            Pay for a plan
          </Link>
        </div>
      )}

      {/* Verification badge notification */}
      {verification?.verification_status === 'verified' ? (
        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <Icon name="BadgeCheck" size={22} className="text-emerald-600 mt-0.5" />
            <div>
              <p className="font-semibold text-emerald-900 text-sm">Verified agent badge active</p>
              <p className="text-xs text-emerald-800/80">
                Your profile shows a trusted badge to users on the notice board and marketplace.
              </p>
            </div>
          </div>
          <Link
            to="/agent-dashboard/profile"
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-emerald-300 bg-white text-emerald-800 text-sm font-medium hover:bg-emerald-50"
          >
            View profile
          </Link>
        </div>
      ) : (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <Icon name="ShieldAlert" size={22} className="text-amber-600 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-900 text-sm">Build trust with verification</p>
              <p className="text-xs text-amber-800/80">
                Verified agents rank higher with clients and unlock stronger notice-board confidence.
                Status: <strong>{verification?.verification_status || 'none'}</strong>
              </p>
            </div>
          </div>
          <Link
            to="/agent-dashboard/verification"
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700"
          >
            Get verified
          </Link>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
        <Kpi label="Total properties" value={totalListings} icon="Building2" />
        <Kpi label="Active properties" value={activeListings} icon="CheckCircle2" />
        <Kpi label="Open CRM leads" value={openLeads || workspace?.crm?.leads_open || 0} icon="Contact" />
        <Kpi label="Active co-brokers" value={activeDeals || workspace?.co_broker?.active || 0} icon="Users" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Tools grid */}
        <div className="lg:col-span-2 space-y-6">
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-slate-900">Agent toolkit</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {tools.map((t) => (
                <Link
                  key={t.to}
                  to={t.to}
                  className="group rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-300 hover:shadow-md transition-all"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${t.accent} mb-3`}>
                    <Icon name={t.icon} size={20} />
                  </div>
                  <p className="font-semibold text-slate-900 group-hover:text-slate-700">{t.title}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{t.desc}</p>
                </Link>
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-slate-900">Recent properties</h2>
              <Link to="/agent-dashboard/properties" className="text-sm text-amber-700 font-medium hover:underline">
                Manage all
              </Link>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              {propsLoading ? (
                <p className="p-6 text-sm text-slate-500">Loading properties…</p>
              ) : properties.length === 0 ? (
                <div className="p-8 text-center">
                  <Icon name="Building2" size={36} className="mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-600 mb-3">No properties yet. Publish your first property.</p>
                  <Link
                    to="/agent-dashboard/properties/new"
                    className="inline-flex px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium"
                  >
                    Add Property
                  </Link>
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {properties.slice(0, 6).map((p) => (
                    <li key={p.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
                      <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                        {p.title_image ? (
                          <img src={p.title_image} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Icon name="Image" size={18} className="text-slate-400" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate">{p.title}</p>
                        <p className="text-xs text-slate-500 truncate">
                          {p.city || p.address || '—'} ·{' '}
                          {p.propery_type == 1 ||
                          p.property_type == 1 ||
                          String(p.propery_type ?? p.property_type).toLowerCase() === 'rent'
                            ? 'For Rent'
                            : 'For Sale'}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-slate-900">
                          {p.price != null ? formatPrice(p.price, 'UGX') : '—'}
                        </p>
                        <span
                          className={`text-[10px] uppercase font-semibold ${
                            Number(p.status) === 1 ? 'text-emerald-600' : 'text-slate-400'
                          }`}
                        >
                          {Number(p.status) === 1 ? 'Live' : 'Draft/Off'}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>

        {/* Side column */}
        <div className="space-y-4">
          <section className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Quick actions</h3>
            <div className="space-y-2">
              <Action to="/agent-dashboard/properties/new" icon="PlusCircle" label="Add Property" />
              <Action to="/agent-dashboard/profile" icon="User" label="My profile" />
              <Action to="/agent-dashboard/verification" icon="ShieldCheck" label="Verification badge" />
              <Action to="/workspace/crm" icon="UserPlus" label="Add CRM lead" />
              <Action to="/workspace/co-broker" icon="FilePlus" label="New co-broker deal" />
              <Action to="/workspace/bookings" icon="CalendarPlus" label="Set availability" />
              <Action to="/notice-board" icon="MessageSquare" label="Browse notice board" />
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-slate-900 text-white p-5">
            <p className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold mb-2">
              Pipeline snapshot
            </p>
            <ul className="space-y-3 text-sm">
              <li className="flex justify-between">
                <span className="text-slate-400">CRM follow-ups due</span>
                <span className="font-semibold">{workspace?.crm?.follow_ups_due ?? 0}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-slate-400">Co-broker pending</span>
                <span className="font-semibold">{workspace?.co_broker?.pending ?? 0}</span>
              </li>
              <li className="flex justify-between">
                <span className="text-slate-400">Upcoming bookings</span>
                <span className="font-semibold">
                  {(workspace?.bookings?.upcoming_as_advisor || 0) +
                    (workspace?.bookings?.upcoming_as_client || 0)}
                </span>
              </li>
              <li className="flex justify-between">
                <span className="text-slate-400">Docs to sign</span>
                <span className="font-semibold">{workspace?.documents?.awaiting_my_signature ?? 0}</span>
              </li>
            </ul>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Professional tips</h3>
            <ul className="text-xs text-slate-600 space-y-2 leading-relaxed">
              <li>• Keep properties accurate with clear media and pricing.</li>
              <li>• Log every lead touchpoint in CRM for follow-through.</li>
              <li>• Use co-broker agreements before sharing exclusive inventory.</li>
              <li>• Complete verification to signal trust on the notice board.</li>
            </ul>
          </section>
        </div>
      </div>
    </AgentLayout>
  );
};

const Kpi = ({ label, value, icon }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
      <Icon name={icon} size={16} className="text-slate-400" />
    </div>
    <p className="text-2xl font-bold text-slate-900">{value}</p>
  </div>
);

const Action = ({ to, icon, label }) => (
  <Link
    to={to}
    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-slate-700 hover:bg-slate-50 border border-transparent hover:border-slate-100"
  >
    <Icon name={icon} size={16} className="text-slate-500" />
    {label}
  </Link>
);

export default AgentDashboard;
